/**
 * Öffentliche Mengenrabatt-Kalkulatoren für die Embed-Preisliste.
 *
 * Wird auf der öffentlichen `PublicPricingPage` gerendert, wenn der User
 * (nicht eingeloggt) im Tab „Punkt-Pakete" / „Service-Abo" landet.
 *
 * Spiegelt die Kunden-Portal-Logik (`VolumeDiscountCalculator` /
 * `ServiceAboCalculator`) auf reine Read-Only-Variante:
 *   - Slider + Eingabefeld → Live-Preis-Update via Public API
 *   - „Tipp: Nimm X mehr für Y% Rabatt"-Hinweis (Conversion-Anker)
 *   - Dual-CTA: „Anmelden zum Kaufen" + „Probesession buchen"
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Star, ShoppingCart, Sparkles, Minus, Plus, ChevronDown, Calendar, LogIn } from 'lucide-react';
import { et, etp } from '../../i18n/embedLang';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// shared colors — mirrors PublicPricingPage's `C` palette
const C = {
  accent: '#EAB308',
  text: '#FFFFFF',
  textMuted: '#A3A3A3',
  textSubtle: '#737373',
  cardBg: '#171717',
  border: '#262626',
};

const headingFont = "'Space Grotesk', sans-serif";
const fontFamily = "'Inter', sans-serif";

const debounce = (fn, ms = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const formatCurrency = (val, currency = 'CHF') =>
  `${currency} ${Number(val || 0).toFixed(2)}`;

/** Dual-CTA Box — gleiche Layout in beiden Kalkulatoren wiederverwendet. */
function DualCTA({ loginUrl, bookingUrl, contextLabel }) {
  return (
    <div
      style={{
        marginTop: 14,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
      }}
      data-testid="public-shop-dual-cta"
    >
      <a
        href={loginUrl}
        target="_top"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '10px 12px',
          background: C.accent,
          color: '#000',
          fontFamily: headingFont,
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: 'none',
        }}
        data-testid="public-shop-login-cta"
      >
        <LogIn style={{ width: 14, height: 14 }} />
        Bereits Kunde? Anmelden
      </a>
      <a
        href={bookingUrl}
        target="_top"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '10px 12px',
          background: 'transparent',
          color: C.accent,
          border: `1px solid ${C.accent}`,
          fontFamily: headingFont,
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 8,
          textDecoration: 'none',
        }}
        data-testid="public-shop-booking-cta"
      >
        <Calendar style={{ width: 14, height: 14 }} />
        {et('Neu? Probesession')}
      </a>
    </div>
  );
}

/** Tipp-Hinweis (Conversion-Anker) — analog Customer-Portal. */
function UpsellTip({ recommendation }) {
  if (!recommendation) return null;
  return (
    <div
      style={{
        marginTop: 10,
        background: 'linear-gradient(90deg, rgba(234,179,8,0.10), rgba(34,197,94,0.10))',
        border: '1px solid rgba(234,179,8,0.30)',
        borderRadius: 10,
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
      }}
      data-testid="public-shop-upsell-tip"
    >
      <Sparkles style={{ width: 14, height: 14, color: C.accent, marginTop: 2, flexShrink: 0 }} />
      <div>
        <p style={{ color: C.accent, fontSize: 11, fontWeight: 700, margin: 0 }}>
          {et('Tipp:')} {recommendation.target_label || ''}
        </p>
        <p style={{ color: C.textMuted, fontSize: 11, margin: '2px 0 0 0' }}>
          {recommendation.message}
        </p>
      </div>
    </div>
  );
}

/**
 * Punkte-Kalkulator (öffentlich).
 * Slider von 1 bis zur höchsten konfigurierten Tier-Stufe.
 */
export function PublicPointsCalculator({ loginUrl, bookingUrl, currency = 'CHF', onBuyClick }) {
  const [points, setPoints] = useState(20);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastReq = useRef(0);

  const fetchCalc = useCallback(
    debounce(async (p) => {
      const reqId = ++lastReq.current;
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/pricing/volume-calculator?points=${p}`);
        if (reqId === lastReq.current) setCalc(r.data);
      } catch {
        // soft-fail
      } finally {
        if (reqId === lastReq.current) setLoading(false);
      }
    }, 200),
    []
  );

  useEffect(() => { fetchCalc(points); }, [points, fetchCalc]);

  const sliderMax = (() => {
    const tiers = calc?.all_tiers || [];
    if (!tiers.length) return 100;
    const top = [...tiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0)).slice(-1)[0];
    return top.max_points != null ? Number(top.max_points) : Number(top.min_points) || 100;
  })();

  return (
    <div
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 16,
      }}
      data-testid="public-points-calculator"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Star style={{ width: 18, height: 18, color: C.accent }} />
        <p style={{ fontFamily: headingFont, color: C.text, fontSize: 14, fontWeight: 700, margin: 0 }}>
          {et('Punkte-Paket selbst wählen')}
        </p>
      </div>

      <p style={{ fontSize: 11, color: C.textSubtle, margin: '0 0 12px 0' }}>
        {et('Stelle dir dein Punkte-Guthaben selbst zusammen — je mehr du nimmst, desto grösser dein Vorteil.')}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button
          onClick={() => setPoints(Math.max(1, points - 5))}
          style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: C.text, border: 'none', cursor: 'pointer' }}
          aria-label="5 weniger"
        >
          <Minus style={{ width: 14, height: 14 }} />
        </button>
        <input
          type="range"
          min="1"
          max={sliderMax}
          value={Math.min(points, sliderMax)}
          onChange={(e) => setPoints(parseInt(e.target.value, 10))}
          style={{ flex: 1, accentColor: C.accent }}
          data-testid="public-points-slider"
        />
        <input
          type="number"
          min="1"
          value={points}
          onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value, 10) || 1))}
          style={{
            width: 56,
            padding: '6px 8px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.08)',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontSize: 12,
            textAlign: 'center',
          }}
          data-testid="public-points-input"
        />
        <button
          onClick={() => setPoints(points + 5)}
          style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: C.text, border: 'none', cursor: 'pointer' }}
          aria-label="5 mehr"
        >
          <Plus style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
        <div>
          <p style={{ fontSize: 11, color: C.textSubtle, margin: 0 }}>{et('Endpreis')}</p>
          {calc?.discount_percent > 0 && (
            <p style={{ fontSize: 11, color: C.textSubtle, textDecoration: 'line-through', margin: '2px 0 0 0' }}>
              {formatCurrency(calc?.full_price, currency)}
            </p>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontFamily: headingFont, color: C.accent, fontSize: 22, fontWeight: 700, margin: 0, opacity: loading ? 0.5 : 1 }}>
            {formatCurrency(calc?.final_price, currency)}
          </p>
          {calc?.discount_percent > 0 && (
            <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, margin: '2px 0 0 0' }}>
              −{Math.round(calc.discount_percent)}% {calc.tier_label}
            </p>
          )}
        </div>
      </div>

      <UpsellTip recommendation={calc?.recommendation} />

      {onBuyClick ? (
        <button
          onClick={() => onBuyClick({
            type: 'volume_points',
            id: String(points),
            name: etp('{0} Punkte', [points]),
            price: calc?.final_price || 0,
          })}
          disabled={!calc || loading}
          data-testid="calc-points-buy"
          style={{
            marginTop: 16, width: '100%',
            padding: '13px 20px',
            background: C.accent, color: '#000',
            border: 'none', borderRadius: 10,
            fontFamily: headingFont, fontWeight: 700, fontSize: 13,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: (!calc || loading) ? 'wait' : 'pointer',
            opacity: (!calc || loading) ? 0.6 : 1,
            transition: 'all .2s ease',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {et('JETZT KAUFEN')} →
        </button>
      ) : (
        <DualCTA loginUrl={loginUrl} bookingUrl={bookingUrl} contextLabel={etp('{0} Punkte', [points])} />
      )}
    </div>
  );
}

/**
 * Service-Abo-Kalkulator (öffentlich).
 * Service-Dropdown + Anzahl-Slider + Live-Preis.
 */
export function PublicServiceAboCalculator({ loginUrl, bookingUrl, currency = 'CHF', onBuyClick }) {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastReq = useRef(0);

  useEffect(() => {
    axios.get(`${API}/public/pricing/service-abo-services`)
      .then(r => {
        const list = r.data?.services || [];
        setServices(list);
        if (list.length && !serviceId) setServiceId(list[0].id);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCalc = useCallback(
    debounce(async (sid, q) => {
      if (!sid) return;
      const reqId = ++lastReq.current;
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/pricing/service-abo-calculator?service_id=${sid}&quantity=${q}`);
        if (reqId === lastReq.current) setCalc(r.data);
      } catch {
        // soft-fail
      } finally {
        if (reqId === lastReq.current) setLoading(false);
      }
    }, 200),
    []
  );

  useEffect(() => {
    if (serviceId) fetchCalc(serviceId, quantity);
  }, [serviceId, quantity, fetchCalc]);

  const sliderMax = (() => {
    const tiers = calc?.all_tiers || [];
    if (!tiers.length) return 50;
    const top = [...tiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0)).slice(-1)[0];
    return top.max_points != null ? Number(top.max_points) : Number(top.min_points) || 50;
  })();

  return (
    <div
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 16,
      }}
      data-testid="public-service-abo-calculator"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ShoppingCart style={{ width: 18, height: 18, color: C.accent }} />
        <p style={{ fontFamily: headingFont, color: C.text, fontSize: 14, fontWeight: 700, margin: 0 }}>
          {et('Service-Abo selbst kombinieren')}
        </p>
      </div>

      <p style={{ fontSize: 11, color: C.textSubtle, margin: '0 0 12px 0' }}>
        {et('Wähle deinen Service + Anzahl Einheiten — Staffelrabatt wird automatisch berechnet.')}
      </p>

      {services.length > 0 && (
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <select
            value={serviceId}
            onChange={e => setServiceId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 28px 10px 12px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.08)',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontSize: 13,
              fontFamily,
              appearance: 'none',
              cursor: 'pointer',
            }}
            data-testid="public-service-abo-select"
          >
            {services.map(s => (
              <option key={s.id} value={s.id} style={{ background: '#000', color: C.text }}>
                {s.name} ({formatCurrency(s.price_per_unit, currency)}{et('/Einheit')})
              </option>
            ))}
          </select>
          <ChevronDown style={{ position: 'absolute', right: 10, top: 12, width: 14, height: 14, color: C.textSubtle, pointerEvents: 'none' }} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button
          onClick={() => setQuantity(Math.max(1, quantity - 5))}
          style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: C.text, border: 'none', cursor: 'pointer' }}
          aria-label="5 weniger"
        >
          <Minus style={{ width: 14, height: 14 }} />
        </button>
        <input
          type="range"
          min="1"
          max={sliderMax}
          value={Math.min(quantity, sliderMax)}
          onChange={e => setQuantity(parseInt(e.target.value, 10))}
          style={{ flex: 1, accentColor: C.accent }}
          data-testid="public-service-abo-slider"
        />
        <input
          type="number"
          min="1"
          value={quantity}
          onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
          style={{
            width: 56,
            padding: '6px 8px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.08)',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontSize: 12,
            textAlign: 'center',
          }}
          data-testid="public-service-abo-input"
        />
        <button
          onClick={() => setQuantity(quantity + 5)}
          style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: C.text, border: 'none', cursor: 'pointer' }}
          aria-label="5 mehr"
        >
          <Plus style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
        <div>
          <p style={{ fontSize: 11, color: C.textSubtle, margin: 0 }}>{et('Endpreis')}</p>
          {calc?.discount_percent > 0 && (
            <p style={{ fontSize: 11, color: C.textSubtle, textDecoration: 'line-through', margin: '2px 0 0 0' }}>
              {formatCurrency(calc?.full_price, currency)}
            </p>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontFamily: headingFont, color: C.accent, fontSize: 22, fontWeight: 700, margin: 0, opacity: loading ? 0.5 : 1 }}>
            {formatCurrency(calc?.final_price, currency)}
          </p>
          {calc?.discount_percent > 0 && (
            <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, margin: '2px 0 0 0' }}>
              −{Math.round(calc.discount_percent)}% {calc.tier_label}
            </p>
          )}
        </div>
      </div>

      <UpsellTip recommendation={calc?.recommendation} />

      {onBuyClick ? (
        <button
          onClick={() => {
            const svcName = services.find(s => s.id === serviceId)?.name || 'Service';
            onBuyClick({
              type: 'volume_service_abo',
              id: `${serviceId}:${quantity}`,
              name: `${quantity}× ${svcName}`,
              price: calc?.final_price || 0,
            });
          }}
          disabled={!calc || !serviceId || loading}
          data-testid="calc-abo-buy"
          style={{
            marginTop: 16, width: '100%',
            padding: '13px 20px',
            background: C.accent, color: '#000',
            border: 'none', borderRadius: 10,
            fontFamily: headingFont, fontWeight: 700, fontSize: 13,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: (!calc || !serviceId || loading) ? 'wait' : 'pointer',
            opacity: (!calc || !serviceId || loading) ? 0.6 : 1,
            transition: 'all .2s ease',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {et('JETZT KAUFEN')} →
        </button>
      ) : (
        <DualCTA
          loginUrl={loginUrl}
          bookingUrl={bookingUrl}
          contextLabel={`${quantity}x ${services.find(s => s.id === serviceId)?.name || ''}`}
        />
      )}
    </div>
  );
}
